import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import {
  ArrowUp,
  BookOpen,
  CircleHelp,
  FileSpreadsheet,
  FileText,
  LoaderCircle,
  Pencil,
  Plus,
  Square,
  X,
} from 'lucide-react';
import { AI_CHAT_ATTACHMENT_ACCEPT } from '@repo/lib';
import { Button } from '@dashboard/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@dashboard/components/ui/popover';
import { Textarea } from '@dashboard/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@dashboard/components/ui/toggle-group';
import getPageTypeLabel from '@dashboard/services/getPageTypeLabel';
import { filterGuideGroups } from './aiGuides';
import GuideMenu from './GuideMenu';
import PanelTooltip from './PanelTooltip';
import useChatAttachments from './useChatAttachments';

const PLACEHOLDERS = {
  edit: 'Describe a change, or type / for a guide',
  ask: 'Ask a question. Switch to Edit to make changes.',
};

const MODE_ITEM_CLASS =
  'h-6.5 px-2.5 text-xs text-muted-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-xs';

const MENU_KEYS = ['ArrowDown', 'ArrowUp', 'Enter'];
const NO_GUIDES = [];

const guideQueryOf = value => (/^\/\S*$/.test(value) ? value.slice(1) : null);

const filesOf = dataTransfer => [...(dataTransfer?.files ?? [])];

function AttachmentPreview({ attachment }) {
  const pending = attachment.fileId ? '' : 'opacity-50';
  if (attachment.kind === 'image') {
    return (
      <img
        src={attachment.preview}
        alt={attachment.name}
        className={`size-12 rounded-md border object-cover ${pending}`}
      />
    );
  }
  const Icon = attachment.type === 'text/csv' ? FileSpreadsheet : FileText;
  return (
    <span
      className={`flex h-12 max-w-40 items-center gap-1.5 rounded-md border px-2.5 text-xs ${pending}`}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      <span className="truncate">{attachment.name}</span>
    </span>
  );
}

function AttachedFiles({ attachments, onRemove }) {
  return (
    <ul aria-label="Attached files" className="flex flex-wrap gap-2 pt-1">
      {attachments.map(attachment => (
        <li key={attachment.id} className="relative">
          <AttachmentPreview attachment={attachment} />
          {!attachment.fileId && (
            <LoaderCircle
              role="status"
              aria-label={`Uploading ${attachment.name}`}
              className="absolute inset-0 m-auto size-4 animate-spin"
            />
          )}
          <Button
            type="button"
            variant="outline"
            size="icon-xs"
            aria-label={`Remove ${attachment.name}`}
            className="absolute -right-1.5 -top-1.5 size-5 rounded-full bg-background"
            onClick={() => onRemove(attachment.id)}
          >
            <X aria-hidden="true" />
          </Button>
        </li>
      ))}
    </ul>
  );
}

// The ref is the message box, so the panel can fill it and put the cursor at the end.
const Composer = forwardRef(function Composer(
  {
    mode,
    pageType,
    onModeChange,
    value,
    onChange,
    onSubmit,
    onStop,
    isStreaming,
    guideCard,
    guideGroups = NO_GUIDES,
    onSelectGuide,
    isGuidesOpen,
    onToggleGuides,
  },
  ref,
) {
  const textareaRef = useRef(null);
  useImperativeHandle(ref, () => textareaRef.current, []);
  const menuRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isMenuDismissed, setIsMenuDismissed] = useState(false);
  const chatAttachments = useChatAttachments();
  const text = value.trim();
  const canSend =
    (text.length > 0 || chatAttachments.attachments.length > 0) &&
    !chatAttachments.isUploading &&
    !isStreaming;
  const guideQuery = guideQueryOf(value);
  const isMenuOpen =
    guideQuery !== null &&
    !isMenuDismissed &&
    filterGuideGroups(guideGroups, guideQuery).length > 0;

  const submit = () => {
    if (!canSend) return;
    onSubmit(text, chatAttachments.uploaded);
    chatAttachments.clear();
  };

  const attachFrom = (event, dataTransfer) => {
    const files = filesOf(dataTransfer);
    if (files.length === 0) return;
    event.preventDefault();
    chatAttachments.add(files);
  };

  const selectGuide = key => {
    onSelectGuide(key);
    onChange('');
    textareaRef.current?.focus();
  };

  const handleKeyDown = event => {
    if (event.nativeEvent.isComposing) return;
    // Focus stays in the message box, so the menu's own key handling gets these keys forwarded.
    // Shift+Enter still adds a new line, which closes the menu.
    if (isMenuOpen && MENU_KEYS.includes(event.key) && !(event.key === 'Enter' && event.shiftKey)) {
      event.preventDefault();
      menuRef.current?.dispatchEvent(
        new KeyboardEvent('keydown', { key: event.key, bubbles: true }),
      );
      return;
    }
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    submit();
  };

  const pageLabel = getPageTypeLabel({ type: pageType });

  return (
    <div className="@container shrink-0 px-4 pb-4 pt-3">
      <Popover open={isMenuOpen} onOpenChange={open => !open && setIsMenuDismissed(true)}>
        <PopoverAnchor asChild>
          <form
            className="flex flex-col gap-2 rounded-xl border border-input bg-background py-2.5 pl-3 pr-2.5 shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50"
            onSubmit={event => {
              event.preventDefault();
              submit();
            }}
            onDragOver={event => {
              if (event.dataTransfer?.types?.includes('Files')) event.preventDefault();
            }}
            onDrop={event => attachFrom(event, event.dataTransfer)}
          >
            {guideCard}
            {chatAttachments.attachments.length > 0 && (
              <AttachedFiles
                attachments={chatAttachments.attachments}
                onRemove={chatAttachments.remove}
              />
            )}
            <Textarea
              ref={textareaRef}
              aria-label="Message the AI editor"
              rows={2}
              value={value}
              placeholder={PLACEHOLDERS[mode] ?? PLACEHOLDERS.edit}
              className="max-h-40 min-h-10 resize-none rounded-none border-0 p-0 shadow-none focus-visible:ring-0"
              onChange={event => {
                setIsMenuDismissed(false);
                onChange(event.target.value);
              }}
              onKeyDown={handleKeyDown}
              onPaste={event => attachFrom(event, event.clipboardData)}
            />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              data-testid="ai-attachment-input"
              accept={AI_CHAT_ATTACHMENT_ACCEPT}
              onChange={event => {
                chatAttachments.add(event.target.files);
                event.target.value = '';
              }}
            />
            {/* The labels give way to icons when the chat column is too narrow for them. */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-1">
                <PanelTooltip label="Add files or photos">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Add files or photos"
                    className="text-muted-foreground"
                    disabled={chatAttachments.isFull}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Plus aria-hidden="true" />
                  </Button>
                </PanelTooltip>
                <ToggleGroup
                  type="single"
                  size="sm"
                  spacing={0.5}
                  aria-label="Mode"
                  value={mode}
                  className="rounded-lg bg-muted p-0.5"
                  onValueChange={next => next && onModeChange(next)}
                >
                  <PanelTooltip label={`Ask about this ${pageLabel}`}>
                    <ToggleGroupItem value="ask" aria-label="Ask" className={MODE_ITEM_CLASS}>
                      <CircleHelp aria-hidden="true" />
                      <span className="hidden @[14.5rem]:inline">Ask</span>
                    </ToggleGroupItem>
                  </PanelTooltip>
                  <PanelTooltip label={`Edit this ${pageLabel}`}>
                    <ToggleGroupItem value="edit" aria-label="Edit" className={MODE_ITEM_CLASS}>
                      <Pencil aria-hidden="true" />
                      <span className="hidden @[14.5rem]:inline">Edit</span>
                    </ToggleGroupItem>
                  </PanelTooltip>
                </ToggleGroup>
                {onToggleGuides && (
                  <PanelTooltip label="Browse guides">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label="Guides"
                      aria-expanded={isGuidesOpen}
                      className="text-muted-foreground"
                      onClick={onToggleGuides}
                    >
                      <BookOpen aria-hidden="true" />
                      <span className="hidden @[18rem]:inline">Guides</span>
                    </Button>
                  </PanelTooltip>
                )}
              </div>
              {isStreaming ? (
                <Button type="button" variant="outline" onClick={onStop}>
                  <Square aria-hidden="true" className="fill-current" />
                  Stop
                </Button>
              ) : (
                <Button type="submit" size="icon" aria-label="Send message" disabled={!canSend}>
                  <ArrowUp aria-hidden="true" />
                </Button>
              )}
            </div>
          </form>
        </PopoverAnchor>
        <PopoverContent
          side="top"
          align="start"
          sideOffset={6}
          className="w-[var(--radix-popover-trigger-width)] p-1.5"
          onOpenAutoFocus={event => event.preventDefault()}
          onCloseAutoFocus={event => event.preventDefault()}
        >
          <GuideMenu
            ref={menuRef}
            query={guideQuery ?? ''}
            groups={guideGroups}
            onSelect={selectGuide}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
});

export default Composer;
